import { ArrowLeft01Icon, Cancel01Icon } from '@hugeicons/core-free-icons';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  Colors,
  Header,
  HeaderButton,
  HeaderTitle,
  Loader,
  LogoIcon,
  ProgressBar,
  TextLight,
  TextReg,
  useAuthStore,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { runOnJS } from 'react-native-reanimated';
import { ConfirmEmailPage } from './ConfirmEmailPage';
import { EmailLoginForm } from './EmailLoginForm';
import { EmailPasswordLoginForm } from './EmailPasswordLoginForm';
import { ModalStepHeader } from './ModalStepHeader';

type AuthStep = 'login' | 'confirm' | 'signup' | 'login-long';

type AuthStepConfig = {
  headerText: string;
  subHeaderText: string;
  footer?: { text: string; linkText: string; onPress: () => void };
};

type StepContainerProps = {
  config: AuthStepConfig;
  children: React.ReactNode;
  currentStep?: number;
  totalSteps?: number;
  showProgressBar?: boolean;
};

function StepContainer({
  config,
  children,
  currentStep,
  totalSteps,
  showProgressBar = false,
}: StepContainerProps) {
  const styles = useDynamicStyles(createStyles);
  const { footer, headerText, subHeaderText } = config;
  const { text, linkText, onPress } = footer ?? {};

  return (
    <>
      {showProgressBar && currentStep && totalSteps && (
        <View style={styles.progressContainer}>
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        </View>
      )}
      <ModalStepHeader headerText={headerText} subHeaderText={subHeaderText} />
      {children}
      {footer && (
        <TextLight>
          {text}
          <TextReg onPress={onPress} style={styles.linkText}>
            {linkText}
          </TextReg>
        </TextLight>
      )}
    </>
  );
}

/**
 * Auth modal content (login/signup/confirm forms).
 * Used inside AuthFlowModal parent component.
 */
export function AuthModalContent() {
  const { modalFlow, authModal, dismissModalFlow, updateAuthModal } =
    useAuthStore();
  const { isDismissible } = modalFlow;
  const { currentStep, previousStep } = authModal;
  const styles = useDynamicStyles(createStyles);

  const doubleTap = Gesture.Tap()
    .numberOfTaps(2)
    .maxDuration(500)
    .maxDistance(50)
    .onEnd(() => {
      runOnJS(updateAuthModal)({
        currentStep: 'login-long',
      });
    });

  const handleBack = () => {
    // Smart back navigation based on previous substage
    updateAuthModal({
      currentStep: previousStep || 'login',
      codeSent: false,
    });
  };

  const handleHide = () => {
    if (isDismissible) {
      dismissModalFlow();
    }
  };

  const stepConfigs: Record<AuthStep, AuthStepConfig> = {
    login: {
      headerText: 'Log In',
      subHeaderText: "You've got this",
      footer: {
        text: 'No account yet? ',
        linkText: 'Sign Up',
        onPress: () => updateAuthModal({ currentStep: 'signup' }),
      },
    },
    signup: {
      headerText: 'Sign Up',
      subHeaderText: "You're one step closer to your next big thing",
      footer: {
        text: 'Already have an account? ',
        linkText: 'Log In',
        onPress: () => updateAuthModal({ currentStep: 'login' }),
      },
    },
    'login-long': {
      headerText: 'Log In',
      subHeaderText: "You've got this",
      footer: {
        text: '',
        linkText: 'Go back to simple login',
        onPress: () => updateAuthModal({ currentStep: 'login' }),
      },
    },
    confirm: {
      headerText: 'Confirm your email.',
      subHeaderText:
        'We just sent you a 6 digit code via email. You know the drill.',
    },
  };

  return (
    <>
      <Header
        headerTitle={
          <GestureDetector gesture={doubleTap}>
            <View style={styles.headerLogoContainer}>
              <HeaderTitle logo={LogoIcon} />
            </View>
          </GestureDetector>
        }
        headerLeft={
          currentStep === 'confirm' && (
            <HeaderButton icon={ArrowLeft01Icon} onPress={handleBack} />
          )
        }
        headerRight={
          isDismissible ? (
            <HeaderButton icon={Cancel01Icon} onPress={handleHide} />
          ) : undefined
        }
        showShadow={currentStep === 'login' || currentStep === 'login-long'}
      />
      <View style={styles.content}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {currentStep === 'login' && (
            <StepContainer config={stepConfigs.login}>
              <EmailLoginForm />
            </StepContainer>
          )}

          {currentStep === 'signup' && (
            <StepContainer
              config={stepConfigs.signup}
              currentStep={1}
              totalSteps={3}
              showProgressBar
            >
              <EmailLoginForm isSignUp />
            </StepContainer>
          )}

          {currentStep === 'login-long' && (
            <StepContainer config={stepConfigs['login-long']}>
              <EmailPasswordLoginForm />
            </StepContainer>
          )}

          {currentStep === 'confirm' && (
            <StepContainer
              config={stepConfigs['confirm']}
              currentStep={2}
              totalSteps={3}
              showProgressBar
            >
              <ConfirmEmailPage />
            </StepContainer>
          )}

          {!currentStep && <Loader />}
        </ScrollView>
      </View>
    </>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    content: {
      flex: 1,
      padding: isMobile ? 12 : 24,
      alignItems: 'center',
    },
    scrollContainer: {
      flex: 1,
      width: '100%',
      paddingHorizontal: 12,
    },
    scrollContent: {
      alignItems: 'center',
      flexGrow: 1,
      gap: isMobile ? 20 : 30,
    },
    headerLogoContainer: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 40,
      minWidth: 100,
    },
    linkText: {
      color: Colors.purple1,
    },
    progressContainer: {
      width: '100%',
      marginBottom: isMobile ? 8 : 16,
    },
  });
